import { useEffect, useMemo } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import ProtectedRoutes from "./utils/ProtectedRoutes";
import SignIn from "./pages/SignIn";
import Register from "./pages/RegisterForm";
import Dashboard from "./pages/Dashboard";
import Dashboard2 from "./pages/Dashboard2";
import Regions from "./pages/Regions";
import Places from "./pages/Places";
import Devices from "./pages/Devices";
import Users from "./pages/Users";
import Reports from "./pages/Reports";
import Stocks from "./pages/Stock";
import NodeDetail from "./pages/NodeDetail";
import Telemetry from "./pages/Telemetry";
import { RefreshIntervalProvider } from "./contexts/RefreshIntervalContext";
import { SidebarProvider } from "./contexts/SidebarContext";
import { getCurrentTenant } from "./config/tenantConfig";
import AdminConfigurations from "./pages/AdminConfigurations";
import CreateRoute from "./pages/CreateRoute";
import VendingEvents from "./pages/VendingEvents";
import ProjectList from "./pages/ProjectList";
import ErrorBoundary from "./components/ErrorBoundary";
import ErrorTestButton from "./components/ErrorTestButton";
import VersionUpdatePopup from "./components/VersionUpdatePopup";
import ConfirmRole from "./pages/ConfirmRole";
import ClientSelect from "./pages/ClientSelect";
import DigitalWallet from "./pages/DigitalWallet";
import DigitalPay from "./pages/DigitalPay";
import { getMuiDataGridLocaleText } from "./i18n/muiDataGridLocale";
import { DP_FONT_FAMILY } from "./pages/digital-pay/constants";

function App() {
  const { t, i18n } = useTranslation();
  // Get the tenant configuration based on the current hostname
  const tenantConfig = getCurrentTenant();
  const { primary } = tenantConfig.theme;
  // PERSONAL (Vendai Digital Pay): kendi ekran seti. Login + tema ortak, eski tenant
  // sayfalarının hiçbiri bağlı değil; bağlanmamış her yol "*" üzerinden köke,
  // oradan /dashboard'a, o da Personel listesine döner.
  const isVendaiDigitalPay = tenantConfig.projectCode === "PERSONAL";

  // Set document title based on tenant
  useEffect(() => {
    document.title = tenantConfig.title || tenantConfig.name;

    // Update favicon if tenant has one
    if (tenantConfig.favicon) {
      const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';
      link.href = tenantConfig.favicon;
      document.getElementsByTagName('head')[0].appendChild(link);
    }
  }, [tenantConfig.title, tenantConfig.name, tenantConfig.favicon]);

  // Create MUI theme based on tenant's primary color
  const muiTheme = useMemo(
    () =>
      createTheme({
    palette: {
      primary: { main: primary },
    },
    typography: isVendaiDigitalPay
      ? {
          fontFamily: DP_FONT_FAMILY,
          fontSize: 14,
          body1: { lineHeight: 1.55, letterSpacing: "0.005em" },
          body2: { lineHeight: 1.5, letterSpacing: "0.005em" },
          button: {
            fontFamily: DP_FONT_FAMILY,
            fontWeight: 650,
            letterSpacing: "-0.005em",
          },
        }
      : undefined,
    components: {
      // Tüm DataGrid'lerde boş durumda "No rows" yerine özel mesaj.
      MuiDataGrid: {
        defaultProps: {
          localeText: getMuiDataGridLocaleText(i18n.language || i18n.resolvedLanguage, {
            noRowsLabel: t("common.noRows", { defaultValue: "Veri bulunamadı" }),
            noResultsOverlayLabel: t("common.noRows", {
              defaultValue: "Veri bulunamadı",
            }),
          }),
        },
        styleOverrides: isVendaiDigitalPay
          ? {
              root: {
                fontFamily: DP_FONT_FAMILY,
                fontVariantNumeric: "tabular-nums",
              },
            }
          : undefined,
      },
      ...(isVendaiDigitalPay && {
        MuiButton: {
          styleOverrides: {
            root: { fontFamily: DP_FONT_FAMILY },
          },
        },
        MuiInputBase: {
          styleOverrides: {
            root: {
              fontFamily: DP_FONT_FAMILY,
              fontSize: "0.9rem",
              letterSpacing: "0.005em",
            },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: {
              fontFamily: DP_FONT_FAMILY,
              fontSize: "0.9rem",
            },
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: {
              fontFamily: DP_FONT_FAMILY,
              fontSize: "0.9rem",
            },
          },
        },
      }),
    },
      }),
    [i18n.language, i18n.resolvedLanguage, isVendaiDigitalPay, primary, t]
  );

  return (
    <ThemeProvider theme={muiTheme}>
      <ErrorBoundary>
        <VersionUpdatePopup />
        <Router>
          <SidebarProvider>
          <RefreshIntervalProvider>
            <Routes>
              {/* Root redirects based on authToken and companyName in localStorage */}
              <Route
                path="/"
                element={
                  (() => {
                    const authToken = localStorage.getItem("authToken");
                    const companyName = localStorage.getItem("companyName");

                    if (!authToken) {
                      return <Navigate to="/login" />;
                    }

                    // If user has DefaultClient or no client selected, go to client selection
                    if (!companyName || companyName === "DefaultClient") {
                      return <Navigate to="/client" />;
                    }

                    // Check if companyName has corresponding data in localStorage
                    const companyData = localStorage.getItem(companyName);
                    if (!companyData) {
                      return <Navigate to="/client" />;
                    }

                    // User has a valid client with data, go to dashboard
                    return <Navigate to="/dashboard" />;
                  })()
                }
              />

              {/* Pass tenant information to the SignIn component */}
              <Route
                path="/login"
                element={<SignIn tenantConfig={tenantConfig} />}
              />

              {/* Register route - only for default tenant */}
              {tenantConfig.name === "LettaIoT" && (
                <Route
                  path="/register"
                  element={<Register tenantConfig={tenantConfig} />}
                />
              )}

              {/* Confirm Role - login required but no sidebar */}
              <Route path="/confirmRole" element={<ConfirmRole />} />

              {/* Client Select - for choosing or creating a client after confirmRole */}
              <Route path="/client" element={<ClientSelect />} />

              {/* Protected routes */}
              <Route element={<ProtectedRoutes />}>
                {isVendaiDigitalPay ? (
                  // Hepsi tek sayfa: DigitalPay kendi sidebar'ını taşır,
                  // route yalnızca hangi panelin gösterileceğini seçer.
                  <>
                    <Route path="/dashboard" element={<DigitalPay />} />
                    <Route path="/cards" element={<DigitalPay />} />
                    <Route path="/auth-companies" element={<DigitalPay />} />
                    <Route path="/firms" element={<DigitalPay />} />
                    <Route path="/departments" element={<DigitalPay />} />
                    <Route path="/personal" element={<DigitalPay />} />
                    {/* Geçici olarak kapalı: Limit Tanımları UI ve API isteği devre dışı. */}
                    {/* <Route path="/limit-definitions" element={<DigitalPay />} /> */}
                    <Route path="/payments" element={<DigitalPay />} />
                    <Route path="/transactions" element={<DigitalPay />} />
                  </>
                ) : (
                  <>
                    <Route path="/dashboard" element={<Dashboard />} />
                    {/* Gizli route: menüde/tab'da yok, sadece URL ile erişilir */}
                    <Route path="/dashboard2" element={<Dashboard2 />} />
                    <Route path="/regions" element={<Regions />} />
                    {/* RCU: /places shows the live node list (NodeDetail); other
                        tenants keep the places management grid (Places). */}
                    <Route
                      path="/places"
                      element={
                        tenantConfig.projectCode === "RCU" ? (
                          <NodeDetail />
                        ) : (
                          <Places />
                        )
                      }
                    />
                    <Route path="/places/:idNode" element={<Telemetry />} />
                    {/* RCU: places management grid lives here. */}
                    <Route path="/place-definitions" element={<Places />} />
                    <Route path="/vending-definitions" element={<Places />} />
                    <Route path="/vending-events" element={<VendingEvents />} />
                    <Route path="/vending-machines/:idNode" element={<Telemetry />} />
                    <Route path="/vending-machines" element={<NodeDetail />} />
                    <Route path="/node-detail" element={<NodeDetail />} />
                    <Route path="/stock-management" element={<Stocks />} />
                    <Route path="/devices" element={<Devices />} />
                    <Route
                      path="/admin-configurations"
                      element={<AdminConfigurations />}
                    />
                    <Route path="/users" element={<Users />} />
                    <Route path="/digital-wallet" element={<DigitalWallet />} />
                    <Route path="/create-route" element={<CreateRoute />} />
                  </>
                )}
              </Route>
              {!isVendaiDigitalPay && (
                <>
                  <Route path="/projects" element={<ProjectList />} />
                  <Route path="/reports" element={<Reports />} />
                </>
              )}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
            {/* Error Boundary test butonu - sadece development */}
            {process.env.NODE_ENV === "development" && <ErrorTestButton />}
          </RefreshIntervalProvider>
          </SidebarProvider>
        </Router>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
